/**
 * Coordinate formatter — renders a WGS84 ``(lat, lon)`` pair in the
 * operator's preferred coordinate format (ADR-033 §7).
 *
 * Meridian stores positions as decimal-degree WGS84; the UI converts at
 * render time per :type:`CoordinateFormat` from the active Profile's
 * ``units.coordinate_format`` (REQ-UI-084). Three formats:
 *
 *   - ``decimal_degrees`` — ``"<lat>°, <lon>°"``, default 5 decimals (≈1.1 m
 *     at the equator). Longitude is normalized into ``[-180, 180]`` so a pan
 *     across world copies never surfaces an out-of-range value, and
 *     negative-zero (``-0.000…``) is collapsed to a plain zero. (This is the
 *     logic previously in ``map/format-coordinates.ts``, folded in here as
 *     the single coordinate-rendering home for the units subsystem.)
 *   - ``dms`` — ``"DD°MM'SS\"<N|S>, DDD°MM'SS\"<E|W>"`` with whole seconds,
 *     hemisphere letters from the sign, latitude degrees zero-padded to 2 and
 *     longitude degrees to 3. Minutes/seconds use ASCII ``'`` / ``"`` (NOT the
 *     U+2032/U+2033 primes) so the on-map glyph atlas can render them — the
 *     primes broke the whole annotation-label symbol layer (#10).
 *   - ``mgrs`` — the Military Grid Reference System token at 1 m accuracy via
 *     the ``mgrs`` package, grouped ``GZD 100km E N`` for readability.
 *     ``mgrs.forward`` throws in the polar regions (lat > 84° or < −80°) and
 *     on invalid input; on ANY throw we fall back to the ``decimal_degrees``
 *     rendering so a readout never crashes (operator-visibility, CLAUDE.md
 *     §8.1).
 *
 * Cross-references:
 *   - REQ-UI-084 (coordinate-format preference); ADR-033 §7.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     (the decimal readout this preserves).
 */
import { forward } from "mgrs";
import type { CoordinateFormat } from "@/auth/session-types";

/** Default decimal places for the ``decimal_degrees`` format. */
const DEFAULT_PRECISION = 5;

/** MGRS accuracy in digits (5 ⇒ 1 m easting/northing resolution). */
const MGRS_ACCURACY = 5;

/**
 * Wrap a longitude into the canonical ``[-180, 180]`` range. MapLibre's
 * ``MapMouseEvent.lngLat`` can report longitudes outside that range after a
 * pan across world copies; callers should always render a canonical value.
 */
function normalizeLongitude(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

/** Render one signed-degree axis at fixed precision, collapsing the
 *  ``-0.000…`` artifact to a plain zero. */
function formatSignedDegrees(value: number, precision: number): string {
  const fixed = value.toFixed(precision);
  const zero = (0).toFixed(precision);
  return fixed === `-${zero}` ? zero : fixed;
}

/** Decimal-degrees rendering — also the MGRS polar/invalid fallback. */
function formatDecimal(lat: number, lon: number, precision: number): string {
  const normLon = normalizeLongitude(lon);
  return `${formatSignedDegrees(lat, precision)}°, ${formatSignedDegrees(normLon, precision)}°`;
}

/**
 * Render one axis in degrees/minutes/whole-seconds with a hemisphere letter.
 *
 * @param value - The signed decimal degrees for this axis.
 * @param degreePad - Zero-pad width for the degrees field (2 for latitude,
 *   3 for longitude).
 * @param positive - Hemisphere letter for a non-negative value (``N`` / ``E``).
 * @param negative - Hemisphere letter for a negative value (``S`` / ``W``).
 */
function formatDmsAxis(
  value: number,
  degreePad: number,
  positive: string,
  negative: string,
): string {
  const hemisphere = value < 0 ? negative : positive;
  const abs = Math.abs(value);
  let degrees = Math.floor(abs);
  let minutes = Math.floor((abs - degrees) * 60);
  let seconds = Math.round((abs - degrees - minutes / 60) * 3600);
  // Carry whole-second rounding up through minutes/degrees so we never emit
  // 60″ or 60′ (e.g. 59.9996° must read 60°00′00″, not 59°59′60″).
  if (seconds === 60) {
    seconds = 0;
    minutes += 1;
  }
  if (minutes === 60) {
    minutes = 0;
    degrees += 1;
  }
  const dd = String(degrees).padStart(degreePad, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return `${dd}°${mm}'${ss}"${hemisphere}`;
}

/** Degrees/minutes/seconds rendering, latitude first. */
function formatDms(lat: number, lon: number): string {
  const normLon = normalizeLongitude(lon);
  const latPart = formatDmsAxis(lat, 2, "N", "S");
  const lonPart = formatDmsAxis(normLon, 3, "E", "W");
  return `${latPart}, ${lonPart}`;
}

/**
 * Group a raw MGRS token (``10SEG6187350571``) into
 * ``GZD 100km-square easting northing`` (``10S EG 61873 50571``) for
 * readability. At a fixed accuracy the easting+northing occupy the final
 * ``2 × accuracy`` characters and the 100 km square the two before them; the
 * grid-zone designator (variable width — e.g. ``10S`` vs ``7X``) is whatever
 * precedes that. If the token is shorter than expected (it should never be at
 * this accuracy), the raw token is returned unchanged rather than mis-sliced.
 */
function groupMgrs(token: string): string {
  const numericLen = MGRS_ACCURACY * 2;
  // 2 grid-square chars + the numeric easting/northing block.
  if (token.length < numericLen + 3) {
    return token;
  }
  const northing = token.slice(token.length - MGRS_ACCURACY);
  const easting = token.slice(token.length - numericLen, token.length - MGRS_ACCURACY);
  const square = token.slice(token.length - numericLen - 2, token.length - numericLen);
  const gzd = token.slice(0, token.length - numericLen - 2);
  return `${gzd} ${square} ${easting} ${northing}`;
}

/** MGRS rendering, falling back to decimal on any conversion error. */
function formatMgrs(lat: number, lon: number, precision: number): string {
  try {
    // ``mgrs.forward`` self-normalizes longitude internally; pass the raw lon.
    return groupMgrs(forward([lon, lat], MGRS_ACCURACY));
  } catch {
    // Polar (lat > 84° / < −80°) or invalid input: never crash a readout —
    // degrade to the decimal rendering (operator-visibility, CLAUDE.md §8.1).
    return formatDecimal(lat, lon, precision);
  }
}

/** Options for {@link formatCoordinate}. */
export interface FormatCoordinateOptions {
  /** Decimal places for the ``decimal_degrees`` format (default 5). Ignored
   *  by ``dms`` (whole seconds) and ``mgrs`` (fixed 1 m accuracy), but used
   *  for the MGRS polar fallback. */
  precision?: number;
}

/**
 * Format a WGS84 ``(lat, lon)`` pair in the operator's preferred coordinate
 * format (REQ-UI-084).
 *
 * @param lat - Latitude in decimal degrees (north positive).
 * @param lon - Longitude in decimal degrees (east positive); normalized into
 *   ``[-180, 180]`` for the decimal + DMS renderings.
 * @param format - The target coordinate format (from
 *   ``units.coordinate_format``).
 * @param opts - Optional ``precision`` override for ``decimal_degrees``.
 * @returns The formatted coordinate string.
 */
export function formatCoordinate(
  lat: number,
  lon: number,
  format: CoordinateFormat,
  opts?: FormatCoordinateOptions,
): string {
  const precision = opts?.precision ?? DEFAULT_PRECISION;
  switch (format) {
    case "dms":
      return formatDms(lat, lon);
    case "mgrs":
      return formatMgrs(lat, lon, precision);
    case "decimal_degrees":
    default:
      return formatDecimal(lat, lon, precision);
  }
}
